import React, { useEffect, useRef, useState } from 'react';

// Global hover-meny: visas på alla kort som innehåller en graf (svg) eller tabell.
// Kopiera bild · Spara PNG · (Excel om kortet har en tabell). Mountas en gång i App.
export default function CardActions() {
  const [card, setCard] = useState(null);
  const [rect, setRect] = useState(null);
  const [busy, setBusy] = useState('');
  const [panel, setPanel] = useState(null); // { loading } | { text } | { fel } — Förklara-svaret
  const cardRef = useRef(null);
  const barRef = useRef(null);
  const panelRef = useRef(null);
  const panelOpenRef = useRef(false);
  panelOpenRef.current = !!panel;

  useEffect(() => {
    const qualifies = (c) => c && !c.hasAttribute('data-noexport') && c.querySelector('svg, .period-table, table');
    const onOver = (e) => {
      if (panelOpenRef.current) return; // förklaringen öppen → lås kortvalet tills den stängs
      if (barRef.current && barRef.current.contains(e.target)) return; // över menyn → behåll
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      const c = e.target.closest ? e.target.closest('.card') : null;
      if (c && qualifies(c)) {
        if (c !== cardRef.current) { cardRef.current = c; setCard(c); setRect(c.getBoundingClientRect()); setBusy(''); }
      } else if (cardRef.current) {
        cardRef.current = null; setCard(null);
      }
    };
    const onScrollResize = () => {
      if (cardRef.current) setRect(cardRef.current.getBoundingClientRect());
    };
    document.addEventListener('mouseover', onOver);
    window.addEventListener('scroll', onScrollResize, true);
    window.addEventListener('resize', onScrollResize);
    return () => {
      document.removeEventListener('mouseover', onOver);
      window.removeEventListener('scroll', onScrollResize, true);
      window.removeEventListener('resize', onScrollResize);
    };
  }, []);

  if (!card || !rect) return null;
  if (rect.bottom < 50 || rect.top > window.innerHeight - 10) return null; // utanför vy

  // Bildexporten renderar kortets DOM och bryr sig inte om innehållet, så
  // Kopiera och PNG erbjuds på ALLA kort. Tidigare krävdes ett <svg> i kortet,
  // vilket gjorde att rena tabellkort bara kunde laddas ner som Excel.
  const hasTable = !!card.querySelector('.period-table, table');
  const titleEl = card.querySelector('.card-title');
  const name = ((titleEl ? titleEl.textContent : 'kort') || 'kort')
    .trim().slice(0, 44).replace(/[^\wåäöÅÄÖ \-–]/g, '').replace(/\s+/g, '-').toLowerCase() || 'kort';

  const run = async (kind) => {
    const node = cardRef.current; if (!node) return;
    setBusy(kind);
    try {
      if (kind === 'copy') {
        const m = await import('../utils/exportImage'); await m.copyNodePng(node);
        setBusy('copied'); setTimeout(() => setBusy(b => b === 'copied' ? '' : b), 1500); return;
      }
      if (kind === 'png')  { const m = await import('../utils/exportImage'); await m.exportNodePng(node, name); }
      if (kind === 'xlsx') { const m = await import('../utils/exportTable'); m.exportCardTable(node, name); }
      setBusy('');
    } catch (e) { setBusy(''); alert('Export misslyckades: ' + (e?.message || e)); }
  };

  // ✨ Förklara: skicka kortets synliga text till AI:n och visa tolkningen
  const forklaraKort = async () => {
    const node = cardRef.current; if (!node || panel?.loading) return;
    setPanel({ loading: true });
    try {
      const titel = (node.querySelector('.card-title')?.textContent || '').trim();
      const block = (node.closest('.screen')?.querySelector('.header-name')?.textContent
        || node.closest('.screen')?.querySelector('.header-title')?.textContent || '').trim();
      const innehall = (node.innerText || '').slice(0, 12000);
      const { forklara } = await import('../qlik');
      const res = await forklara({ block, titel, innehall });
      setPanel({ text: res.forklaring });
    } catch (e) {
      setPanel({ fel: e?.message || 'Kunde inte förklara just nu' });
    }
  };

  const top = Math.max(54, rect.top - 28);
  const btn = {
    border: '1px solid var(--border)', borderRadius: 7, cursor: 'pointer',
    fontSize: 11.5, lineHeight: 1, padding: '5px 9px', fontFamily: 'system-ui,sans-serif',
    background: 'var(--card)', color: 'var(--text-secondary)', boxShadow: '0 1px 5px rgba(0,0,0,0.14)', whiteSpace: 'nowrap',
  };
  return (
    <div ref={barRef} data-export-skip="1" data-noprint="1"
      style={{ position: 'fixed', top, left: rect.right - 6, transform: 'translateX(-100%)', zIndex: 700, display: 'flex', gap: 5 }}>
      <button style={{ ...btn, background: '#1d6a5f', color: '#fff', borderColor: '#1d6a5f' }} onClick={() => run('copy')} title="Kopiera bild till urklipp">
        {busy === 'copied' ? '✓ Kopierad' : busy === 'copy' ? '…' : '⧉ Kopiera'}
      </button>
      <button style={btn} onClick={() => run('png')} title="Spara som bild (PNG)">{busy === 'png' ? '…' : '⤓ PNG'}</button>
      {hasTable && (
        <button style={btn} onClick={() => run('xlsx')} title="Ladda ner som Excel/Sheets (CSV)">{busy === 'xlsx' ? '…' : '⤓ Excel'}</button>
      )}
      <button style={{ ...btn, background: '#44365C', color: '#fff', borderColor: '#44365C' }}
        onClick={forklaraKort} title="Låt AI:n förklara vad kortet visar">
        {panel?.loading ? '…' : '✨ Förklara'}
      </button>

      {/* Förklaringspanelen — låser kortvalet tills den stängs */}
      {panel && (
        <div ref={panelRef} data-export-skip="1" data-noprint="1"
          style={{
            position: 'absolute', top: 34, right: 0,
            width: Math.min(440, Math.max(300, rect.width - 24)),
            background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 10,
            boxShadow: '0 6px 24px rgba(0,0,0,0.22)', padding: '10px 12px',
            fontFamily: 'system-ui,sans-serif', zIndex: 710,
          }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#44365C' }}>✨ Förklaring</span>
            <button onClick={() => setPanel(null)}
              style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1 }}
              title="Stäng">✕</button>
          </div>
          {panel.loading && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>🤔 Tolkar kortet…</div>}
          {panel.fel && <div style={{ fontSize: 12.5, color: '#A32D2D' }}>{panel.fel}</div>}
          {panel.text && (
            <div style={{ fontSize: 12.5, lineHeight: 1.55, color: 'var(--text)', whiteSpace: 'pre-wrap', maxHeight: 300, overflowY: 'auto' }}>
              {panel.text}
            </div>
          )}
          {panel.text && (
            <div style={{ fontSize: 10.5, color: 'var(--text-secondary)', marginTop: 6 }}>
              AI-tolkning av kortets synliga siffror — dubbelkolla mot tabellen vid viktiga beslut.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
